import PropTypes from 'prop-types'

import { cn } from '@/utils/helpers'

import { Button } from '../elements/button'

/**
 * @typedef {Object} ActionButton
 * @extends {Button}
 * @property {string} type - The type of button (e.g., 'cancel', 'delete', 'submit').
 * @property {string|JSX.Element} label - The label to display on the button.
 */

/**
 * ActionButtons
 * @param {Object} props
 * @param {ActionButton[]} props.actions - An array of action buttons to render.
 * @param {string} [props.className] - Classes to add to the action buttons container.
 * @param {boolean} [props.loading] - Whether to show a loading state on the buttons.
 */
function ActionButtons({ actions, loading = false, className = '' }) {
  const parsedActions = actions.map((action, index) => ({
    id: action.id || `action-button-${index}`,
    ...action,
  }))
  if (parsedActions.length === 0) {
    return null
  }
  return (
    <div className={cn('flex flex-row gap-2', className)}>
      {parsedActions.map(({ type, label, ...rest }) => {
        const key = rest.key || rest.id || label
        if (type === 'cancel') {
          return (
            <Button key={key} variant="secondary" loading={loading} {...rest}>
              {label}
            </Button>
          )
        }
        if (type === 'delete') {
          return (
            <Button key={key} variant="destructive" loading={loading} {...rest}>
              {label}
            </Button>
          )
        }
        if (type === 'submit') {
          return (
            <Button key={key} type="submit" loading={loading} {...rest}>
              {label}
            </Button>
          )
        }
        return (
          <Button key={key} loading={loading} {...rest}>
            {label}
          </Button>
        )
      })}
    </div>
  )
}

export default ActionButtons

ActionButtons.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  className: PropTypes.string,
}
